import { Model } from 'modapp-resource';
import l10n from 'utils/l10n';

const accessTokenRefreshLeadTime = 5 * 60 * 1000;
const accessTokenRetryDelay = 60 * 1000;

const mainRoles = [
	{ id: 'staff', name: l10n.l('auth.admin', "Administratör") },
	{ id: 'sales', name: l10n.l('auth.salesperson', "Säljare") },
	{ id: 'educator', name: l10n.l('auth.educator', "Utbildare") },
	{ id: 'participant', name: l10n.l('auth.participant', "Deltagare") },
	{ id: 'client', name: l10n.l('auth.client', "Beställare") },
];

/**
 * Auth fetches the currently logged in user on start, or redirects to the login
 * page if the user is not logged in.
 */
class Auth {

	constructor(app, params) {
		this.app = app;
		this.params = Object.assign({
			loginUrl: '/auth',
			logoutUrl: '/auth/logout',
			authenticateUrl: '/auth?noredirect',
			actionUrl: '/auth/action',
			redirected: false
		}, params);

		// Bind callbacks
		this._onConnect = this._onConnect.bind(this);
		this._onUnsubscribe = this._onUnsubscribe.bind(this);
		this._beforeConnect = this._beforeConnect.bind(this);
		this._onAccessTokenTimer = this._onAccessTokenTimer.bind(this);
		this._handleAccessTokenRefreshError = this._handleAccessTokenRefreshError.bind(this);

		this.app.require([ 'api' ], this._init.bind(this));
	}

	_init(module) {
		this.module = module;
		this.userPromise = null;
		this.postLoginHandlers = [];
		this.accessTokenExpiresAt = null;
		this.accessTokenTimer = null;
		this.accessTokenRequestPromise = null;
		this.accessTokenRequestForceRefresh = false;
		this._createExtUserPromise();
		this.model = new Model({
			data: {
				loggedIn: false,
				user: null,
				roles: new Model({
					eventBus: this.app.eventBus
				}),
				activeRole: null
			},
			eventBus: this.app.eventBus
		});

		this.module.api.setOnConnect(this._onConnect);
		this.module.api.setBeforeConnect(this._beforeConnect);
	}

	/**
	 * Registers a post login handler with a callback called after login and
	 * prior to the user promise being resolved.
	 * @param {object} handler Callback object
	 * @param {string} handler.id Callback ID.
	 * @param {number} handler.sortOrder Sort order.
	 * @param {(user: import('types/Api').UserModel, roles: Model<Record<string, true>>) => Promise<void> | void} handler.callback Callback called after user is logged in.
	 * @returns {this}
	 */
	addPostLoginHandler(handler) {
		if (this.postLoginHandlers.includes(v => v.id == handler.id)) {
			throw new Error("PostLoginHandler ID already registered: ", handler.id);
		}
		this.postLoginHandlers.push(handler);
		this.postLoginHandlers.sort((a, b) => a.sortOrder - b.sortOrder);
		return this;
	}

	/**
	 * Unregisters a previously registered post login handler.
	 * @param {string} handlerId Handler ID.
	 * @returns {this}
	 */
	removePostLoginHandler(handlerId) {
		let idx = this.postLoginHandlers.findIndex(handlerId);
		if (idx >= 0) {
			this.postLoginHandlers.splice(idx, 1);
		}
		return this;
	}

	/**
	 * Gets the auth model containing the currently logged in user.
	 * @returns {Model} The auth model.
	 */
	getModel() {
		return this.model;
	}

	/**
	 * Gets the auth roles model containing all the roles of the logged in user.
	 * @returns {Model} The roles model.
	 */
	getRoles() {
		return this.model.roles;
	}

	/**
	 * Gets the model of the logged in user.
	 * @returns {UserModel} The user model.
	 */
	getUser() {
		return this.model.user || null;
	}

	/**
	 * Gets a promise to the logged in user.
	 * It resolves as soon as the user is logged in and fetched.
	 * @returns {Promise.<UserModel>} Currently logged in user.
	 */
	getUserPromise() {
		return this.extUserPromise;
	}

	/**
	 * Gets a list of the main roles.
	 * @returns {Array.<object>} Array of role objects: { id, name }
	 */
	getMainRoles() {
		return mainRoles;
	}

	/**
	 * Sets the active role of the user. The role must be a main role held by the user.
	 * @param {string} roleId ID of role.
	 */
	setActiveRole(roleId) {
		for (let r of mainRoles) {
			if (roleId == r.id) {
				if (!this.model.roles[roleId]) {
					console.error("User does not have role: ", roleId);
				} else {
					this.model.set({ activeRole: roleId });
					if (sessionStorage && this.model.user) {
						sessionStorage.setItem('auth.user.' + this.model.user.id + '.activeRole', roleId);
					}
				}
				return;
			}
		}
		throw new Error("Invalid main role: " + roleId);
	}

	/**
	 * Redirects the browser to the login page.
	 */
	redirectToLogin() {
		if (this.params.redirected) {
			console.log("Already redirected");
			return;
		}
		this.params.redirected = true;
		this._clearAccessTokenTimer();
		window.location.replace(this.params.loginUrl + '?redirect_uri=' + encodeURIComponent(window.location.href));
	}

	/**
	 * Returns the url to an action, such as reactivating your account.
	 */
	getActionUrl(token, confirm) {
		return this.params.actionUrl + '?a=' + encodeURIComponent(token) + (confirm ? '&confirm=yes' : '');
	}

	/**
	 * Authenticates the user or redirects to login if not logged in.
	 *
	 * The returned promise will be rejected if the API returns any other error
	 * than 'user.authenticationFailed'. If user.authenticationFailed is
	 * returned, the auth module will redirect the client to the authentication
	 * endpoint.
	 * @param {boolean} [noRedirect] Flag that prevents failed authentication to redirect to login.
	 * @returns {Promise} Promise to the authenticate.
	 */
	authenticate(noRedirect) {
		return this._authenticateRequest(this._shouldForceAccessTokenRefresh()).catch(err => {
			if (!noRedirect && this._isAuthenticationFailure(err)) {
				this.redirectToLogin();
				return null;
			}
			throw err;
		}).then(response => {
			if (response === null) {
				return null;
			}
			return this._getCurrentUser().catch(err => {
				// On access denied, we have failed to authentication and
				// shouldredirect.
				if (!noRedirect && (err.code == 'system.accessDenied' || err.code == 'auth.invalidToken')) {
					this.redirectToLogin();
					return;
				}
				// Rewrite the system.connectionError to a friendly message.
				if (err.code == 'system.connectionError') {
					err = { code: err.code, message: "Det gick inte att ansluta till Nercia API. Kontakta Nercia om felet kvarstår." };
				}
				// Or else we throw the error to be handled by the caller to show an
				// error message.
				throw err;
			});
		});
	}

	/**
	 * Authenticates the browser cookies through the auth endpoint and stores the
	 * access token expiry returned by the server. It never opens a WebSocket.
	 * @param {boolean} forceRefresh Flag that requests a refresh of a valid token.
	 * @returns {Promise.<object>} Authentication response.
	 */
	_authenticateRequest(forceRefresh) {
		forceRefresh = !!forceRefresh;
		if (this.accessTokenRequestPromise) {
			if (forceRefresh && !this.accessTokenRequestForceRefresh) {
				// Refresh tokens can rotate, so wait rather than refreshing in parallel.
				return this.accessTokenRequestPromise.then(() => this._authenticateRequest(true));
			}
			return this.accessTokenRequestPromise;
		}

		this.accessTokenRequestForceRefresh = forceRefresh;
		let url = this.params.authenticateUrl;
		if (forceRefresh) {
			url += (url.includes('?') ? '&' : '?') + 'refresh';
		}
		this.accessTokenRequestPromise = fetch(url, {
			method: 'POST',
			mode: 'cors',
			credentials: 'include',
		}).catch(err => {
			// Rethrow fetch errors as a more friendly message.
			throw { code: 'auth.authFetchFailed', message: "Det gick inte att nå Nercia API. Kontakta Nercia om felet kvarstår.", data: err };
		}).then(resp => {
			if (resp.status >= 400) {
				return resp.json().catch(() => ({
					code: 'auth.invalidResponse',
					message: "Invalid authentication error response"
				})).then(err => {
					if (!err || typeof err != 'object') {
						err = {
							code: 'auth.invalidResponse',
							message: "Invalid authentication error response"
						};
					}
					err.status = resp.status;
					throw err;
				});
			}
			return resp.json().catch(err => {
				throw {
					code: 'auth.invalidResponse',
					message: "Invalid authentication response",
					data: err
				};
			});
		}).then(response => {
			this._setAccessTokenExpiresAt(response.accessTokenExpiresAt);
			return response;
		}).finally(() => {
			this.accessTokenRequestPromise = null;
			this.accessTokenRequestForceRefresh = false;
		});
		return this.accessTokenRequestPromise;
	}

	_beforeConnect() {
		if (!this._needsAccessTokenRefresh()) {
			return Promise.resolve();
		}
		return this._authenticateRequest(this._shouldForceAccessTokenRefresh()).then(() => {
			if (this._shouldForceAccessTokenRefresh()) {
				return this._authenticateRequest(true);
			}
		}).catch(err => {
			this._handleAccessTokenRefreshError(err);
			throw err;
		});
	}

	_needsAccessTokenRefresh() {
		return !this.accessTokenExpiresAt || this._shouldForceAccessTokenRefresh();
	}

	_shouldForceAccessTokenRefresh() {
		return !!this.accessTokenExpiresAt && this.accessTokenExpiresAt - Date.now() <= accessTokenRefreshLeadTime;
	}

	_setAccessTokenExpiresAt(expiresAt) {
		if (typeof expiresAt != 'number' || !Number.isFinite(expiresAt) || expiresAt <= 0) {
			throw {
				code: 'auth.invalidResponse',
				message: "Missing or invalid access token expiry"
			};
		}

		this.accessTokenExpiresAt = expiresAt;
		this._scheduleAccessTokenRefresh(Math.max(
			accessTokenRetryDelay,
			expiresAt - Date.now() - accessTokenRefreshLeadTime
		));
	}

	_onAccessTokenTimer() {
		this.accessTokenTimer = null;
		this._authenticateRequest(true).catch(this._handleAccessTokenRefreshError);
	}

	_scheduleAccessTokenRefresh(delay) {
		this._clearAccessTokenTimer();
		if (!this.params.redirected) {
			this.accessTokenTimer = setTimeout(this._onAccessTokenTimer, delay);
		}
	}

	_handleAccessTokenRefreshError(err) {
		if (this._isAuthenticationFailure(err) || !this.accessTokenExpiresAt || this.accessTokenExpiresAt <= Date.now()) {
			this.redirectToLogin();
			return;
		}
		console.error("[Auth] Failed to refresh access token: ", err);
		this._scheduleAccessTokenRefresh(accessTokenRetryDelay);
	}

	_isAuthenticationFailure(err) {
		return err && ((err.status >= 400 && err.status < 500) || err.code == 'auth.authenticationFailed');
	}

	_clearAccessTokenTimer() {
		if (this.accessTokenTimer) {
			clearTimeout(this.accessTokenTimer);
			this.accessTokenTimer = null;
		}
	}

	/**
	 * Redirects the browser to the login page.
	 */
	logout() {
		window.location.replace(this.params.logoutUrl + '?redirect_uri=' + encodeURIComponent(window.location.href));
	}

	/**
	 * Check if the current user has all of the provided roles.
	 * @param {string|Array.<string>} roles Role(s).
	 * @returns {boolean} True if the user has all roles, otherwise false.
	 */
	hasRoles(roles) {
		if (!roles) {
			return true;
		}
		roles = Array.isArray(roles) ? roles : Array.prototype.slice.call(arguments);
		let rs = this.getRoles();
		for (let r of roles) {
			if (!rs[r]) {
				return false;
			}
		}
		return true;
	}

	getRoleName(role) {
		for (let r of mainRoles) {
			if (r.id == role) {
				return r.name;
			}
		}
		return l10n.l('auth.missingRole', "Saknar roll");
	}

	/**
	 * Check if the current user has any of the provided roles.
	 * @param {string|Array.<string>} roles Role(s).
	 * @returns {boolean} True if the user has any of the roles, otherwise false.
	 */
	hasAnyRole(roles) {
		if (!roles) {
			return false;
		}
		roles = Array.isArray(roles) ? roles : Array.prototype.slice.call(arguments);
		let rs = this.getRoles();
		for (let r of roles) {
			if (rs[r]) {
				return true;
			}
		}
		return false;
	}

	_onConnect() {
		return this.module.api.authenticate('user.sub', 'authenticate')
			.then(roles => {
				// Set the roles model
				let o = Object.assign({}, this.model.roles.props);
				for (let k in o) {
					o[k] = undefined;
				}
				for (let r of roles) {
					o[r] = true;
				}
				this.model.roles.set(o);
				// Reset active role if we lost the currently active one
				if (this.model.activeRole && !o[this.model.activeRole]) {
					this.model.set({ activeRole: this._getMainRole() });
				}
			})
			.catch(() => this._onUnsubscribe());
	}

	_getMainRole() {
		let p = this.model.roles.props;
		for (let r of mainRoles) {
			if (p[r.id]) {
				return r.id;
			}
		}
		return null;
	}

	_getCurrentUser() {
		if (!this.userPromise) {
			this.userPromise = this.module.api.call('user.sub', 'getUser')
				.then(async (user) => {
					// Call post login handlers
					for (let h of this.postLoginHandlers) {
						await h.callback(user, this.model.roles);
					}
					return user;
				})
				.then(user => {
					// Get active role
					let activeRole = this.model.activeRole;
					// Load from sessionStorage if available
					if (!activeRole && sessionStorage) {
						activeRole = sessionStorage.getItem('auth.user.' + user.id + '.activeRole');
					}
					// Get first matching role
					if (!activeRole || !this.model.roles[activeRole]) {
						activeRole = this._getMainRole();
					}
					this.model.set({
						loggedIn: true,
						user,
						activeRole
					});
					user.on('unsubscribe', this._onUnsubscribe);
					this.extUser.resolve(user);
					return user;
				})
				.catch(err => {
					this.userPromise = null;
					this.extUser.reject(err);
					throw err;
				});
		}
		return this.userPromise;
	}

	_onUnsubscribe() {
		// Remove user model
		if (this.model.user) {
			this.model.user.off('unsubscribe', this._onUnsubscribe);
			this.model.set({
				loggedIn: false,
				user: null,
				activeRole: null
			});
			this._createExtUserPromise();
			// Clear roles
			let o = Object.assign({}, this.model.roles.props);
			for (let k in o) {
				o[k] = undefined;
			}
			this.model.roles.set(o);
		}
		this.userPromise = null;
	}

	_createExtUserPromise() {
		this.extUserPromise = new Promise((resolve, reject) => {
			this.extUser = { resolve, reject };
		});
	}

	dispose() {
		this._clearAccessTokenTimer();
		this.module.api.setBeforeConnect(null);
		this._onUnsubscribe();
	}
}

export default Auth;
